import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import AutocompleteInput from './AutocompleteInput';
import { useFormData } from '../hooks/useFormData';

const CATEGORIES = ['Serving', 'Tinware', 'Wheels', 'Electrical', 'Body & Paint', 'Diagnosis', 'AC Service', 'Tires', 'Glass', 'Other'];

const SupplierForm = ({ isOpen, onClose, supplier, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', category: '', customCategory: '', rating: 5, address: '', city: '', phone: '', email: '', hours: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (supplier) {
      const cat = supplier.category || '';
      const isKnown = CATEGORIES.map(c => c.toLowerCase()).includes(cat.toLowerCase());
      setFormData({
        name:           supplier.name || '',
        category:       isKnown ? cat : (cat ? 'Other' : ''),
        customCategory: isKnown ? '' : cat,
        rating:         supplier.rating ?? 5,
        address:        supplier.address || '',
        city:           supplier.city || '',
        phone:          supplier.phone || '',
        email:          supplier.email || '',
        hours:          supplier.hours || '',
        notes:          supplier.notes || '',
      });
    } else {
      setFormData({ name: '', category: '', customCategory: '', rating: 5, address: '', city: '', phone: '', email: '', hours: '', notes: '' });
    }
    setError('');
  }, [supplier, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError('Name is required.'); return; }
    setLoading(true);
    setError('');
    try {
      const resolvedCategory = formData.category === 'Other' && formData.customCategory.trim()
        ? formData.customCategory.trim()
        : formData.category;
      await onSave({ ...formData, category: resolvedCategory, rating: Number(formData.rating) });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fd = useFormData();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={supplier ? 'Edit Supplier' : 'Add Supplier'} size="md">
      <form onSubmit={handleSubmit}>
        {error && (
          <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Supplier Name *</label>
            <AutocompleteInput
              name="name" value={formData.name} onChange={handleChange}
              suggestions={fd.supplierNames}
              required placeholder="e.g. AutoFix Garage"
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Category</label>
            <select name="category" className="form-input" value={formData.category} onChange={handleChange}>
              <option value="">— select —</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {formData.category === 'Other' && (
          <div className="form-group">
            <label className="form-label">Specify category *</label>
            <input
              type="text"
              name="customCategory"
              className="form-input"
              value={formData.customCategory}
              onChange={handleChange}
              required
              placeholder="e.g. Rubber / Seals, Transmission, Brakes…"
              autoFocus
            />
          </div>
        )}

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="form-label">Address</label>
            <input
              type="text" name="address" className="form-input"
              value={formData.address} onChange={handleChange}
              placeholder="e.g. 10 Fabricii Street"
            />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">City</label>
            <AutocompleteInput name="city" value={formData.city} onChange={handleChange}
              suggestions={fd.cities} placeholder="e.g. Cluj-Napoca" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input
              type="tel" name="phone" className="form-input"
              value={formData.phone} onChange={handleChange}
              placeholder="e.g. 0722 123 456"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email" name="email" className="form-input"
              value={formData.email} onChange={handleChange}
              placeholder="e.g. contact@autofix.ro"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Rating (1–5)</label>
            <select name="rating" className="form-input" value={formData.rating} onChange={handleChange}>
              {[1,2,3,4,5].map((n) => (
                <option key={n} value={n}>{n} star{n > 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Working Hours</label>
          <input
            type="text" name="hours" className="form-input"
            value={formData.hours} onChange={handleChange}
            placeholder="e.g. LV: 08:00-17:00 | S: 09:00-14:00"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea
            name="notes" className="form-input"
            value={formData.notes} onChange={handleChange}
            rows={3} placeholder="Optional notes about this supplier"
            style={{ resize: 'vertical' }}
          />
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving…' : supplier ? 'Update Supplier' : 'Add Supplier'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SupplierForm;
